/* =============================================================
   BOOKKEEPING SERVICES FOR PERTH BUSINESSES — Internal links section
   SEO-focused section with short descriptions and links to service pages
   ============================================================= */

import { ArrowRight } from "lucide-react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

const links = [
  {
    title: "Bookkeeper Perth",
    description: "Professional bookkeeping services for Perth small businesses. Weekly reconciliation, BAS, payroll and Xero support.",
    href: "/bookkeeper-perth",
  },
  {
    title: "BAS Agent Perth",
    description: "Registered BAS Agent services for GST, PAYG and ATO compliance. Accurate lodgement, every quarter.",
    href: "/bas-agent-perth",
  },
  {
    title: "Xero Bookkeeper Perth",
    description: "Xero Gold Partner setup, training and ongoing support. Get the most from your cloud accounting software.",
    href: "/xero-bookkeeper-perth",
  },
  {
    title: "Payroll Bookkeeper Perth",
    description: "Full payroll processing including STP, superannuation and PAYGW. Compliant and on time, every pay run.",
    href: "/payroll-bookkeeper-perth",
  },
  {
    title: "Catch-up Bookkeeping Perth",
    description: "Fallen behind on your books? We'll get your records up to date quickly and accurately — no judgement.",
    href: "/catch-up-bookkeeping-perth",
  },
  {
    title: "Bookkeeping for Tradies Perth",
    description: "Bookkeeping designed for tradies and contractors. Simple, practical and built around how you work.",
    href: "/bookkeeping-for-tradies-perth",
  },
];

export default function BookkeepingServicesLinks() {
  const { ref, style } = useScrollAnimation({ type: 'fade', speed: 0.3 });
  return (
    <section ref={ref} style={style} className="py-16 lg:py-20 bg-[oklch(0.22_0.06_250)]">
      <div className="container">
        {/* Section header */}
        <div className="max-w-2xl mb-10">
          <div className="section-label mb-4" style={{ color: 'oklch(0.72 0.12 75)' }}>Our Services</div>
          <h2
            className="text-2xl lg:text-3xl xl:text-4xl text-white mb-4 leading-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Bookkeeping Services for{" "}
            <span className="italic text-[oklch(0.72_0.12_75)]">Perth Businesses</span>
          </h2>
          <p
            className="text-white/60 text-base leading-relaxed"
            style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}
          >
            From day-to-day bookkeeping to BAS lodgement, payroll and Xero support — explore our full range of services for Perth small businesses.
          </p>
        </div>

        {/* Links grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {links.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="group flex flex-col gap-2 rounded-xl p-5 bg-white/5 border border-white/10 hover:bg-white/10 hover:border-[oklch(0.72_0.12_75)/0.5] transition-all duration-200"
            >
              <div className="flex items-center justify-between gap-2">
                <h3
                  className="text-base font-semibold text-white group-hover:text-[oklch(0.72_0.12_75)] transition-colors"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  {link.title}
                </h3>
                <ArrowRight
                  size={14}
                  className="text-white/30 group-hover:text-[oklch(0.72_0.12_75)] group-hover:translate-x-0.5 transition-all flex-shrink-0"
                />
              </div>
              <p
                className="text-white/50 text-sm leading-relaxed"
                style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}
              >
                {link.description}
              </p>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}
